import { loadImg } from "./imageProvider.class";
import SphericProjector from "materialisateur-js/src/SphericProjector";
import {
  BoxGeometry,
  MeshBasicMaterial,
  Mesh,
  Group,
  Object3D,
  TextureLoader,
  Texture,
} from "three";
import { VisiteDefinition } from "./VV.class";
import { mobileCheck, getEventCoords } from "./utils";

const throttleFrequency = 5;
const tolerance = 20;

function colorMatch(c1: number[], c2: number[]) {
  const [r, g, b] = c1;
  const [red, green, blue] = c2;
  return (
    Math.abs(r - red) < tolerance &&
    Math.abs(g - green) < tolerance &&
    Math.abs(b - blue) < tolerance
  );
}
interface MapColor {
  name: string;
  r: number;
  g: number;
  b: number;
}

interface SceneDefinition {
  url: string;
  mapUrl: string;
  mapColors: MapColor[];
}

type SceneOverEvent = {
  event: { x: number; y: number };
  data: any;
};

type MapMask = {
  xmin: number;
  xmax: number;
  ymin: number;
  ymax: number;
  canvas: HTMLCanvasElement;
  barycentre?: { x: number; y: number };
};

type MapMasks = {
  [key: string]: MapMask;
};

type SceneDirection = {
  data: any;
  vector: [number, number];
};

class MappedVVRenderer {
  projector: any;
  img: HTMLImageElement;
  colors: MapColor[];
  renderCanvas: HTMLCanvasElement;
  mapCanvas: HTMLCanvasElement;
  mapCanvasContext: CanvasRenderingContext2D;
  public onColorOver: (evt: SceneOverEvent) => void;
  public onDirectionOver: (evt: SceneOverEvent) => void;
  public onDirectionClic: (evt: SceneOverEvent) => void;
  private mouseMoveThrottleStop: boolean;
  hoveredColor: string;
  hoveredDirection: Mesh;
  masks: MapMasks;
  arrowTexture: Texture;
  definitionFactor: number = 1;
  currentScene: VisiteDefinition;

  constructor() {
    this.renderCanvas = document.createElement("canvas");
    this.mapCanvas = document.createElement("canvas");
    this.mapCanvasContext = this.mapCanvas.getContext("2d", {
      willReadFrequently: true,
    });
    this.projector = new SphericProjector(this.renderCanvas, {});
    const node = this.getDomElement();
    node.style.height = "100%";
    node.style.width = "100%";
    node.style.position = "absolute";
    node.addEventListener("mousemove", this.onMouseMove.bind(this));
    node.addEventListener("click", this.onMouseClic.bind(this));
    node.addEventListener("touchstart", this.onMouseMove.bind(this));
    node.addEventListener("touchstart", this.onMouseClic.bind(this));

    const textureLoader = new TextureLoader();
    this.arrowTexture = textureLoader.load("/fleche-vers-le-haut.png");
    this.arrowTexture.repeat.set(1, 1);
    if (mobileCheck()) {
      this.definitionFactor = 2;
    }
  }

  initMapCanvas(img: HTMLImageElement) {
    this.mapCanvas.width = img.naturalWidth / this.definitionFactor;
    this.mapCanvas.height = img.naturalHeight / this.definitionFactor;
    this.mapCanvasContext.drawImage(
      img,
      0,
      0,
      img.naturalWidth / this.definitionFactor,
      img.naturalHeight / this.definitionFactor
    );
    this.buildMasks();
  }

  initRenderCanvas(img: HTMLImageElement) {
    this.renderCanvas.width = img.naturalWidth / this.definitionFactor;
    this.renderCanvas.height = img.naturalHeight / this.definitionFactor;
    const ctx = this.renderCanvas.getContext("2d");
    ctx.drawImage(
      img,
      0,
      0,
      img.naturalWidth / this.definitionFactor,
      img.naturalHeight / this.definitionFactor
    );
    this.projector.initTexture(this.renderCanvas);
  }

  cleanupProjectorScene() {
    const objects = [...this.projector.scene.children];
    objects.forEach((c: Object3D) => {
      if (c.type === "Group") {
        this.projector.scene.remove(c);
      }
    });
  }

  async setScene(params: VisiteDefinition, mapColors: MapColor[]) {
    const previousCoords = this.currentScene?.coords || {
      rotation: [0, 0, 0],
      position: [0, 0, 0],
    };
    this.currentScene = params;
    const { url, mapUrl, coords } = params;
    const fov = this.projector.camera.fov;
    const [lng, lat] = this.projector.getLongLat();
    this.cleanupProjectorScene();
    return Promise.all([loadImg(url), loadImg(mapUrl)]).then(
      ([img, mapImg]) => {
        this.img = img;
        this.colors = mapColors;
        this.initRenderCanvas(img);
        this.initMapCanvas(mapImg);
        const position = {
          long:
            lng +
            ((previousCoords.rotation[2] - coords.rotation[2]) * Math.PI) / 180,
          lat,
          fov,
        };
        this.projector.initPosition(position);
        this.resize();
      }
    );
  }

  addDirection(direction: SceneDirection, offset: number): void {
    const angle =
      Math.atan(direction.vector[1] / direction.vector[0]) +
      (direction.vector[0] > 0 ? 0 : Math.PI) -
      offset +
      Math.PI;
    const geometry = new BoxGeometry(1, 0, 1);
    const material = new MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      map: this.arrowTexture,
      opacity: 0.3,
    });
    const groupe = new Group();
    const cube = new Mesh(geometry, material);
    cube.position.set(0, -2, 3);
    cube.rotation.set(0, Math.PI, 0);
    cube.userData = direction.data;
    this.projector.camera.near = 0.01;
    groupe.add(cube);
    groupe.rotation.set(0, angle, 0);
    this.projector.scene.add(groupe);
    this.projector.render();
  }

  getDomElement(): HTMLCanvasElement {
    return this.projector.getDomElement();
  }

  resize(): void {
    const canvas = this.getDomElement();
    const parent = canvas.parentNode as HTMLElement;
    if (!parent) return;
    this.projector.resize({
      width: parent.clientWidth,
      height: parent.clientHeight,
    });
  }

  getMapAtUv(uv: { x: number; y: number }): Uint8ClampedArray {
    const { x, y } = uv;
    const coords = {
      x: Math.round(x * this.mapCanvas.width),
      y: Math.round((1 - y) * this.mapCanvas.height),
    };
    const data = this.mapCanvasContext.getImageData(coords.x, coords.y, 1, 1);
    return data.data;
  }

  findBestColorMatch(color: Uint8ClampedArray): MapColor {
    return this.colors.find((c) =>
      colorMatch([c.r, c.g, c.b], [color[0], color[1], color[2]])
    );
  }

  getColorMaskBounds(imageData: ImageData): MapMasks {
    const { height, width, data } = imageData;
    let lastColor: MapColor;
    let currentColor: MapColor;
    const result: MapMasks = this.colors.reduce((acc, c) => {
      return {
        ...acc,
        [c.name]: {
          xmin: Infinity,
          xmax: -1,
          ymin: Infinity,
          ymax: -1,
        },
      };
    }, {});
    let r, g, b;
    let index = 0;
    let x;
    for (let i = 0; i < height; i++) {
      for (let j = 0; j < width; j++) {
        r = data[index];
        g = data[index + 1];
        b = data[index + 2];
        index += 4;
        if (r === 0 && g === 0 && b === 0) {
          continue;
        }

        if (
          lastColor &&
          colorMatch([r, g, b], [lastColor.r, lastColor.g, lastColor.b])
        ) {
          currentColor = lastColor;
        } else {
          currentColor = this.findBestColorMatch(
            new Uint8ClampedArray([r, g, b])
          );
        }
        if (currentColor) {
          lastColor = currentColor;
          let { xmin, xmax, ymin, ymax } = result[currentColor.name];
          if (j === 5999 && xmin > -1) {
            xmin = xmax - width;
            xmax = 0;
          }
          if (xmin < 100 && j > width / 2) {
            x = j - width;
          } else {
            x = j;
          }
          result[currentColor.name] = {
            xmin: Math.min(x, xmin),
            xmax: Math.max(x, xmax),
            ymin: Math.min(i, ymin),
            ymax: Math.max(i, ymax),
            canvas: null,
          };
        }
      }
    }
    for (let k in result) {
      if (result[k].xmin < Infinity) {
        const { xmin, xmax, ymin, ymax } = result[k];
        const barycentre = {
          x: (xmin + xmax) / 2,
          y: (ymin + ymax) / 2,
        };
        result[k].barycentre = barycentre;
      }
    }
    return result;
  }

  drawMask(m: MapMask, imageData: ImageData, color: MapColor) {
    const { height, width, data } = imageData;
    const { xmin, xmax, ymin, ymax, canvas } = m;
    const ctx = canvas.getContext("2d");
    const newImageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const newData = newImageData.data;
    const newWidth = newImageData.width;
    const { r, g, b } = color;
    let x, index, newIndex;
    for (let i = ymin; i <= ymax; i++) {
      for (let j = xmin; j <= xmax; j++) {
        x = j;
        if (x < 0) {
          x = x + width;
        }
        index = (i * width + x) * 4;
        const red = data[index];
        const green = data[index + 1];
        const blue = data[index + 2];
        newIndex = ((i - ymin) * newWidth + j - xmin) * 4;
        if (colorMatch([r, g, b], [red, green, blue])) {
          newData[newIndex] = 100;
          newData[newIndex + 1] = 100;
          newData[newIndex + 2] = 100;
          newData[newIndex + 3] = 255;
        } else {
          newData[newIndex] = 0;
          newData[newIndex + 1] = 0;
          newData[newIndex + 2] = 0;
          newData[newIndex + 3] = 0;
        }
      }
    }
    ctx.putImageData(newImageData, 0, 0);
  }

  buildMasks() {
    const imageData = this.mapCanvasContext.getImageData(
      0,
      0,
      this.mapCanvas.width,
      this.mapCanvas.height
    );
    const masks = this.getColorMaskBounds(imageData);
    for (let k in masks) {
      const { xmin, xmax, ymin, ymax } = masks[k];
      if (xmin !== Infinity) {
        const canvas = document.createElement("canvas");
        canvas.width = xmax - xmin + 1;
        canvas.height = ymax - ymin + 1;
        masks[k].canvas = canvas;
        const color = this.colors.find((c) => c.name === k);
        this.drawMask(masks[k], imageData, color);
      }
    }
    this.masks = masks;
  }

  overColor(key: string): void {
    const ctx = this.renderCanvas.getContext("2d");
    ctx.globalCompositeOperation = "source-over";
    ctx.drawImage(
      this.img,
      0,
      0,
      this.img.naturalWidth / this.definitionFactor,
      this.img.naturalHeight / this.definitionFactor
    );
    if (key && this.masks[key]) {
      const maskCanvas = this.masks[key];
      ctx.globalCompositeOperation = "screen";
      ctx.drawImage(
        maskCanvas.canvas,
        maskCanvas.xmin,
        maskCanvas.ymin,
        maskCanvas.canvas.width,
        maskCanvas.canvas.height
      );
    }
    this.projector.initTexture(this.renderCanvas);
  }

  activateColor(event: SceneOverEvent): void {
    if (this.hoveredColor !== event.data.key) {
      this.overColor(event.data.key);
      this.hoveredColor = event.data.key;
    }
    this.projector.getDomElement().style.cursor = "pointer";
    this.onColorOver && this.onColorOver(event);
  }

  desactivateColor(event: SceneOverEvent) {
    this.projector.getDomElement().style.cursor = "default";
    if (this.hoveredColor) {
      this.overColor(null);
      this.hoveredColor = null;
      this.onColorOver && this.onColorOver(event);
    }
  }

  onMouseMove(event: MouseEvent) {
    if (!this.colors || this.mouseMoveThrottleStop) return;
    const coords = getEventCoords(event);
    const intersects = this.projector.convertPageToWorld(coords);
    if (intersects.length === 1) {
      if (this.hoveredDirection) {
        const material = <MeshBasicMaterial>this.hoveredDirection.material;
        material.opacity = 0.2;
        material.needsUpdate = true;
        this.projector.render();
        this.hoveredDirection = null;
      }
      const color = this.getMapAtUv(intersects[0].uv);
      const nearest = this.findBestColorMatch(color);
      const eventData = nearest
        ? { ...this.masks[nearest.name], key: nearest.name }
        : null;
      if (nearest) {
        this.activateColor({ event, data: eventData });
      } else {
        this.desactivateColor({ event, data: null });
      }
    } else {
      this.hoveredDirection = intersects[0].object;
      const material = <MeshBasicMaterial>this.hoveredDirection.material;
      material.opacity = 1;
      material.needsUpdate = true;
      this.projector.render();
      this.projector.getDomElement().style.cursor = "pointer";
      this.onDirectionOver &&
        this.onDirectionOver({ event, data: this.hoveredDirection.userData });
      // Intersection d'une fleche de direction
    }

    setTimeout(() => {
      this.mouseMoveThrottleStop = false;
    }, 1000 / throttleFrequency);
  }

  onMouseClic(event: MouseEvent) {
    const coords = getEventCoords(event);
    const intersects = this.projector.convertPageToWorld(coords);
    if (intersects.length === 2) {
      this.onDirectionClic &&
        this.onDirectionClic({ event, data: intersects[0].object.userData });
    }
  }
}

export { MappedVVRenderer, MapColor, SceneDirection, SceneOverEvent };
